/**
 * MobileSidebarProvider — shared open/close state for the mobile drawer.
 *
 * The desktop sidebar is always rendered (lg:flex). On mobile (lg:hidden)
 * the Header shows a hamburger button that toggles this context, and the
 * Sidebar additionally renders a fixed-overlay drawer when `open` is true.
 *
 * Auto-closes on:
 *   - route change (so navigating from the drawer collapses it)
 *   - Escape key
 *
 * Locks body scroll while open to avoid background scroll on iOS.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

interface MobileSidebarContextValue {
  open:   boolean
  toggle: () => void
  close:  () => void
}

const MobileSidebarContext = createContext<MobileSidebarContextValue | null>(null)

export function MobileSidebarProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()

  const toggle = useCallback(() => setOpen(o => !o), [])
  const close  = useCallback(() => setOpen(false), [])

  // Collapse the drawer when the user navigates.
  useEffect(() => { setOpen(false) }, [pathname])

  // Escape key + body scroll lock.
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', handler)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handler)
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  return (
    <MobileSidebarContext.Provider value={{ open, toggle, close }}>
      {children}
    </MobileSidebarContext.Provider>
  )
}

export function useMobileSidebar(): MobileSidebarContextValue {
  const ctx = useContext(MobileSidebarContext)
  if (!ctx) {
    // Fallback so non-(app) consumers don't crash; toggles become no-ops.
    return { open: false, toggle: () => {}, close: () => {} }
  }
  return ctx
}
